import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useReactToPrint } from "react-to-print";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { X, Printer, Download, MessageCircle, FileText, FileCode } from "lucide-react";
import { downloadCotizacionPdf } from "@/lib/pdf";
import { openWhatsApp } from "@/lib/whatsapp";
import { money, fechaMX } from "@/lib/format";
import DocumentoVenta from "@/components/DocumentoVenta";
import { descargarFactura } from "@/components/FacturaArchivos";
import { RemisionBotones } from "@/components/RemisionArchivos";

export default function PedidoDocumentoDialog({ pedido, onClose }) {
  const [doc, setDoc] = useState(null);
  const [listas, setListas] = useState([]);
  const printRef = useRef(null);
  const headerRef = useRef(null);
  const bodyRef = useRef(null);
  const docName = pedido?.numero_pedido || "Pedido";
  const handlePrint = useReactToPrint({ contentRef: printRef, documentTitle: docName });

  useEffect(() => { api.get("/price-lists").then(({ data }) => setListas(data || [])).catch(() => {}); }, []);
  useEffect(() => {
    setDoc(null);
    if (!pedido) return;
    api.get(`/pedidos/${pedido.id}/documento`).then(({ data }) => setDoc(data))
      .catch((err) => { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); onClose(); });
  }, [pedido]); // eslint-disable-line react-hooks/exhaustive-deps

  const cot = doc?.cotizacion;
  const cli = doc?.cliente;
  const fecha = pedido?.created_at ? String(pedido.created_at).slice(0, 10) : cot?.fecha;
  const whatsapp = () => {
    downloadCotizacionPdf(bodyRef.current, headerRef.current, docName);
    openWhatsApp("", `Hola ${cli?.nombre_contacto || pedido?.cliente || ""}, le compartimos el pedido ${pedido?.numero_pedido} de Duelas Cooper Slip por un total de ${money(cot?.total)} (IVA incluido). Le adjuntamos el PDF. ¡Gracias!`);
  };

  return (
    <Dialog open={!!pedido} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90dvh] overflow-y-auto overflow-x-auto [&>button.absolute]:hidden" data-testid="pedido-documento-dialog">
        <DialogHeader>
          <div className="flex justify-between items-center pr-8 flex-wrap gap-2">
            <div>
              <DialogTitle>Pedido {pedido?.numero_pedido}</DialogTitle>
              <DialogDescription>{pedido?.cliente} · {fechaMX(fecha)}</DialogDescription>
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <Button variant="outline" size="sm" disabled={!cot} className="text-green-600 border-green-600 hover:bg-green-50 hover:text-green-700" onClick={whatsapp} data-testid="pedido-whatsapp"><MessageCircle className="h-4 w-4 mr-2" /> WhatsApp</Button>
              <Button variant="outline" size="sm" disabled={!cot} onClick={() => downloadCotizacionPdf(bodyRef.current, headerRef.current, docName)} data-testid="pedido-descargar-pdf"><Download className="h-4 w-4 mr-2" /> Descargar PDF</Button>
              <Button variant="outline" size="sm" disabled={!cot} onClick={handlePrint} data-testid="pedido-imprimir"><Printer className="h-4 w-4 mr-2" /> Imprimir</Button>
              <RemisionBotones pedido={pedido} />
              {pedido?.factura_pdf && <Button variant="outline" size="sm" className="text-blue-700 border-blue-700 hover:bg-blue-50" onClick={() => descargarFactura(pedido, "pdf")} data-testid="pedido-factura-pdf"><FileText className="h-4 w-4 mr-2" /> Factura PDF</Button>}
              {pedido?.factura_xml && <Button variant="outline" size="sm" className="text-blue-700 border-blue-700 hover:bg-blue-50" onClick={() => descargarFactura(pedido, "xml")} data-testid="pedido-factura-xml"><FileCode className="h-4 w-4 mr-2" /> Factura XML</Button>}
              <Button variant="outline" size="sm" onClick={onClose} data-testid="pedido-doc-cerrar"><X className="h-4 w-4 mr-2" /> Cerrar</Button>
            </div>
          </div>
        </DialogHeader>
        {!doc && <p className="text-sm text-muted-foreground py-8 text-center">Cargando pedido...</p>}
        {doc && pedido && !cot && <p className="text-sm text-muted-foreground py-8 text-center" data-testid="pedido-sin-cotizacion">Este pedido no tiene una cotización ligada, por lo que no hay productos que mostrar.</p>}
        {cot && pedido && (
          <DocumentoVenta
            detail={cot} cli={cli} listas={listas} printRef={printRef} headerRef={headerRef} bodyRef={bodyRef}
            titulo="PEDIDO" numero={pedido?.numero_pedido} fecha={fecha} esPedido
            encabezadoExtra={<p className="text-sm"><span className="font-bold">Cotización:</span> {cot.cot_no}</p>}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
